import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Paper, 
  Grid,
  CircularProgress
} from '@mui/material';
import { getScanJobStatus } from '../api/images';

// Poll status every 2 seconds
const POLL_INTERVAL = 2000;

const ScanProgressDisplay = ({ jobId, onComplete }) => {
  const [scanJob, setScanJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let timer;
    
    const fetchStatus = async () => {
      try {
        const data = await getScanJobStatus(jobId);
        setScanJob(data);
        setLoading(false);
        
        // Continue polling if job is in progress
        if (data.status === 'PENDING' || data.status === 'RUNNING') {
          timer = setTimeout(fetchStatus, POLL_INTERVAL);
        } else if (data.status === 'COMPLETED' && onComplete) {
          // Call the onComplete callback if provided
          onComplete(data);
        }
      } catch (err) {
        setError(err.message || 'Error fetching job status');
        setLoading(false);
      }
    };
    
    fetchStatus();
    
    return () => {
      clearTimeout(timer);
    };
  }, [jobId, onComplete]);
  
  if (loading && !scanJob) {
    return <CircularProgress />;
  }
  
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }
  
  if (!scanJob) {
    return <Typography>No scan job found</Typography>;
  }
  
  // Calculate progress percentage
  const progress = scanJob.total_files_found > 0 
    ? Math.floor((scanJob.files_processed / scanJob.total_files_found) * 100)
    : 0;
  
  // Format status for display
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'RUNNING': return 'Running';
      case 'COMPLETED': return 'Completed';
      case 'ERROR': return 'Error';
      default: return status;
    }
  };
  
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Scan Progress
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">Status</Typography>
          <Typography variant="body1">{getStatusText(scanJob.status)}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">Files Found</Typography>
          <Typography variant="body1">{scanJob.total_files_found}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">Files Processed</Typography>
          <Typography variant="body1">{scanJob.files_processed}</Typography>
        </Grid>
      </Grid>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Progress</span>
          <span>{progress}%</span>
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 10, borderRadius: 5 }} 
        />
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">New Files Added</Typography>
          <Typography variant="body1">{scanJob.new_files_added}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">Files Updated</Typography>
          <Typography variant="body1">{scanJob.existing_files_updated}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="textSecondary">Failed Files</Typography>
          <Typography variant="body1">{scanJob.failed_files}</Typography>
        </Grid>
      </Grid>
      
      {scanJob.error && (
        <Typography color="error" sx={{ mt: 2 }}>
          Error: {scanJob.error}
        </Typography>
      )}
    </Paper>
  );
};

export default ScanProgressDisplay;